import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, Download, DollarSign, Users, Award, Receipt, Calendar, History, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '../../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const today = () => new Date().toISOString().slice(0, 10);
const weekAgo = () => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); };

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// ───────── Tab: Employee Bonuses ─────────
const BonusesTab = () => {
  const [bonuses, setBonuses] = useState([]);
  const [filter, setFilter] = useState('approved');
  const [payPeriod, setPayPeriod] = useState(today());

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/bonus-requests${filter ? `?status=${filter}` : ''}`, { withCredentials: true });
      setBonuses(data);
    } catch (err) { console.error(err); }
  }, [filter]);

  useEffect(() => { refresh(); }, [refresh]);

  const markPaid = async (id) => {
    try {
      await axios.post(`${API}/admin/bonus-requests/${id}/mark-paid`, { pay_period: payPeriod }, { withCredentials: true });
      toast.success('Marked paid');
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {['approved', 'paid', 'pending', 'rejected'].map((s) => (
          <Button key={s} data-testid={`bonus-filter-${s}`} variant={filter === s ? 'default' : 'outline'} size="sm" onClick={() => setFilter(s)} className={filter === s ? 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]' : 'border-[#262626] text-white hover:bg-[#262626]'}>{s.toUpperCase()}</Button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Label className="text-[#A3A3A3] text-xs">Pay period (for new payouts)</Label>
          <Input data-testid="pay-period" type="date" value={payPeriod} onChange={(e) => setPayPeriod(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white w-40" />
        </div>
      </div>
      <div className="space-y-2">
        {bonuses.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded p-8 text-center text-[#A3A3A3]">No bonuses</div>
        ) : bonuses.map((b) => (
          <div key={b.id} className="bg-[#141414] border border-[#262626] rounded p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-white font-medium">{b.user_name} <span className="text-[#A3A3A3] text-xs">— {b.reason}</span></p>
              <p className="text-[#525252] text-xs mt-1">
                {new Date(b.created_at).toLocaleDateString()} · status: <span className="text-[#D4AF37] font-semibold">{b.status}</span>
                {b.pay_period && ` · paid period ${b.pay_period}`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[#D4AF37] font-bold text-lg">{money(b.final_amount || b.proposed_amount)}</p>
              {b.status === 'approved' && (
                <Button data-testid={`mark-paid-${b.id}`} size="sm" onClick={() => markPaid(b.id)} className="bg-green-600 hover:bg-green-700 text-white mt-1"><DollarSign className="w-3 h-3 mr-1" /> Mark Paid</Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ───────── Tab: VIP Referral Payouts ─────────
const VipPayoutsTab = () => {
  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/referrals`, { withCredentials: true }).then(({ data }) => setReferrals(data)).catch(console.error);
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/referrals/${id}`, { status }, { withCredentials: true });
      toast.success(`Marked ${status}`);
      const { data } = await axios.get(`${API}/admin/referrals`, { withCredentials: true });
      setReferrals(data);
    } catch (err) { toast.error('Failed'); }
  };

  const approved = referrals.filter(r => r.status === 'approved');
  const paid = referrals.filter(r => r.status === 'paid');
  const totalOwed = approved.reduce((s, r) => s + Number(r.bonus_amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#141414] border border-[#262626] rounded p-4">
          <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Owed (Approved)</p>
          <p className="text-2xl font-bold text-[#D4AF37]">{money(totalOwed)}</p>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded p-4">
          <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Awaiting Payment</p>
          <p className="text-2xl font-bold text-white">{approved.length}</p>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded p-4">
          <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Paid (lifetime)</p>
          <p className="text-2xl font-bold text-green-400">{paid.length}</p>
        </div>
      </div>
      <div className="bg-[#141414] border border-[#262626] rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] border-b border-[#262626] text-[#A3A3A3]">
            <tr><th className="text-left px-4 py-2">VIP</th><th className="text-left px-4 py-2">Customer / Invoice</th><th className="text-right px-4 py-2">Bonus</th><th className="text-left px-4 py-2">Status</th><th className="text-right px-4 py-2">Action</th></tr>
          </thead>
          <tbody>
            {referrals.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-[#A3A3A3]">No referrals</td></tr>
            ) : referrals.map((r) => (
              <tr key={r.id} className="border-b border-[#262626] last:border-0">
                <td className="px-4 py-2 text-white">{r.vip_name || '—'}</td>
                <td className="px-4 py-2 text-[#A3A3A3]">{r.customer_name} · INV-{r.invoice_number}</td>
                <td className="px-4 py-2 text-right text-[#D4AF37] font-bold">{money(r.bonus_amount)}</td>
                <td className="px-4 py-2 text-[#A3A3A3]">{r.status}</td>
                <td className="px-4 py-2 text-right">
                  {r.status === 'approved' && <Button data-testid={`vip-pay-${r.id}`} size="sm" onClick={() => updateStatus(r.id, 'paid')} className="bg-green-600 hover:bg-green-700 text-white">Mark Paid</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ───────── Tab: Customer Credits ─────────
const CreditsTab = () => {
  const [credits, setCredits] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: '', amount: '', reason: '' });

  const refresh = useCallback(async () => {
    try {
      const [c, cu] = await Promise.all([
        axios.get(`${API}/admin/customer-credits`, { withCredentials: true }),
        axios.get(`${API}/customers`, { withCredentials: true }),
      ]);
      setCredits(c.data);
      setCustomers(cu.data.filter(c => !c.merged_into));
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const grant = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/customer-credits`, { ...form, amount: Number(form.amount) }, { withCredentials: true });
      toast.success(`Credit of ${money(form.amount)} granted`);
      setForm({ customer_id: '', amount: '', reason: '' });
      setOpen(false);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const activeBalance = credits.filter(c => c.status === 'active').reduce((s, c) => s + Number(c.remaining || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="bg-[#141414] border border-[#262626] rounded p-4 flex-1 min-w-[240px]">
          <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Outstanding Credit Balance</p>
          <p className="text-2xl font-bold text-[#D4AF37]">{money(activeBalance)}</p>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button data-testid="grant-credit-btn" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-1" /> Grant Credit</Button>
          </SheetTrigger>
          <SheetContent className="bg-[#0D0D0D] border-[#262626] text-white">
            <SheetHeader><SheetTitle className="text-[#D4AF37]">Grant Customer Credit</SheetTitle></SheetHeader>
            <form onSubmit={grant} className="mt-5 space-y-3">
              <div>
                <Label className="text-white">Customer</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({...form, customer_id: v})}>
                  <SelectTrigger data-testid="credit-customer" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Pick customer" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626] max-h-72">
                    {customers.map(c => <SelectItem key={c.id} value={c.id} className="text-white">{c.name} — {c.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-white">Amount ($)</Label><Input data-testid="credit-amount" type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div><Label className="text-white">Reason</Label><Textarea data-testid="credit-reason" value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} required rows={3} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="Goodwill credit, service issue, referral reward, etc." /></div>
              <Button data-testid="credit-submit" type="submit" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] w-full">Grant Credit</Button>
              <p className="text-[#525252] text-xs">Credit applies automatically to the customer's next paid invoice.</p>
            </form>
          </SheetContent>
        </Sheet>
      </div>
      <div className="bg-[#141414] border border-[#262626] rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] border-b border-[#262626] text-[#A3A3A3]">
            <tr><th className="text-left px-4 py-2">Customer</th><th className="text-left px-4 py-2">Reason</th><th className="text-right px-4 py-2">Granted</th><th className="text-right px-4 py-2">Remaining</th><th className="text-left px-4 py-2">Status</th></tr>
          </thead>
          <tbody>
            {credits.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-[#A3A3A3]">No credits granted</td></tr>
            ) : credits.map((c) => (
              <tr key={c.id} className="border-b border-[#262626] last:border-0">
                <td className="px-4 py-2 text-white">{c.customer_name}</td>
                <td className="px-4 py-2 text-[#A3A3A3]">{c.reason}</td>
                <td className="px-4 py-2 text-right text-white">{money(c.amount)}</td>
                <td className="px-4 py-2 text-right text-[#D4AF37] font-bold">{money(c.remaining)}</td>
                <td className="px-4 py-2 text-[#A3A3A3]">{c.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ───────── Tab: Payroll Export ─────────
const PayrollExportTab = () => {
  const [start, setStart] = useState(weekAgo());
  const [end, setEnd] = useState(today());
  const [department, setDepartment] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start, end });
      if (department) params.set('department', department);
      const { data } = await axios.get(`${API}/admin/payroll/period-summary?${params}`, { withCredentials: true });
      setSummary(data);
    } catch (err) { toast.error('Failed to load'); }
    setLoading(false);
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({ start, end });
      if (department) params.set('department', department);
      const res = await axios.get(`${API}/admin/payroll/export?${params}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `payroll_${start}_to_${end}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-white text-xs">Period start</Label><Input data-testid="period-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white w-40" /></div>
        <div><Label className="text-white text-xs">Period end</Label><Input data-testid="period-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white w-40" /></div>
        <div>
          <Label className="text-white text-xs">Department</Label>
          <select data-testid="period-dept" value={department} onChange={(e) => setDepartment(e.target.value)} className="bg-[#0A0A0A] border border-[#262626] text-white text-sm rounded px-3 py-2 h-10">
            <option value="">All</option>
            <option value="service">Service</option>
            <option value="new_construction">New Construction</option>
            <option value="office">Office</option>
          </select>
        </div>
        <Button data-testid="period-load" onClick={loadSummary} disabled={loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{loading ? 'Loading...' : 'Run Summary'}</Button>
        <Button data-testid="period-export" onClick={exportCsv} variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
      </div>
      {summary && (
        <div className="bg-[#141414] border border-[#262626] rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] border-b border-[#262626] text-[#A3A3A3]">
              <tr>
                <th className="text-left px-4 py-2">Employee</th>
                <th className="text-left px-4 py-2">Role</th>
                <th className="text-right px-4 py-2">Hours</th>
                <th className="text-right px-4 py-2">Rate</th>
                <th className="text-right px-4 py-2">Regular Pay</th>
                <th className="text-right px-4 py-2">Bonus Paid</th>
                <th className="text-right px-4 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.map((r) => (
                <tr key={r.user_id} className="border-b border-[#262626] last:border-0">
                  <td className="px-4 py-2 text-white">{r.name}</td>
                  <td className="px-4 py-2 text-[#A3A3A3] text-xs">{r.role}</td>
                  <td className="px-4 py-2 text-right text-white">{r.hours}</td>
                  <td className="px-4 py-2 text-right text-[#A3A3A3]">{money(r.pay_rate)}</td>
                  <td className="px-4 py-2 text-right text-white">{money(r.regular_pay)}</td>
                  <td className="px-4 py-2 text-right text-[#D4AF37]">{money(r.bonus_paid)}</td>
                  <td className="px-4 py-2 text-right font-bold text-[#D4AF37]">{money(r.total_pay)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ───────── Tab: 1099 Tracking ─────────
const Tax1099Tab = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/1099-tracking?year=${year}`, { withCredentials: true });
      setData(r.data);
    } catch { /* ignore */ }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Label className="text-white">Tax Year</Label>
        <Input data-testid="tax-year" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="bg-[#0A0A0A] border-[#262626] text-white w-32" />
        <span className="text-[#525252] text-xs">Threshold: $600 (1099-NEC)</span>
      </div>
      {data && (
        <div className="bg-[#141414] border border-[#262626] rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] border-b border-[#262626] text-[#A3A3A3]">
              <tr><th className="text-left px-4 py-2">VIP</th><th className="text-right px-4 py-2">Total</th><th className="text-right px-4 py-2">Paid</th><th className="text-right px-4 py-2">Unpaid</th><th className="text-left px-4 py-2">1099 Required?</th></tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-[#A3A3A3]">No referral payouts this year</td></tr>
              ) : data.rows.map((r) => (
                <tr key={r.vip_id} className="border-b border-[#262626] last:border-0">
                  <td className="px-4 py-2 text-white">{r.name}<br/><span className="text-[#525252] text-xs">{r.email}</span></td>
                  <td className="px-4 py-2 text-right text-[#D4AF37] font-bold">{money(r.total)}</td>
                  <td className="px-4 py-2 text-right text-green-400">{money(r.paid)}</td>
                  <td className="px-4 py-2 text-right text-[#A3A3A3]">{money(r.approved_unpaid)}</td>
                  <td className="px-4 py-2">{r.needs_1099 ? <span className="text-red-400 font-bold">YES</span> : <span className="text-[#525252]">No</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ───────── Tab: Payout History ─────────
const HistoryTab = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/payouts/history`, { withCredentials: true }).then(({ data }) => setItems(data)).catch(console.error);
  }, []);
  return (
    <div className="bg-[#141414] border border-[#262626] rounded overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#0A0A0A] border-b border-[#262626] text-[#A3A3A3]">
          <tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">Recipient</th><th className="text-left px-4 py-2">Reference</th><th className="text-right px-4 py-2">Amount</th></tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={5} className="px-4 py-6 text-center text-[#A3A3A3]">No payouts yet</td></tr>
          ) : items.map((it) => (
            <tr key={`${it.type}-${it.id}`} className="border-b border-[#262626] last:border-0">
              <td className="px-4 py-2 text-[#A3A3A3] text-xs">{it.date ? new Date(it.date).toLocaleDateString() : '—'}</td>
              <td className="px-4 py-2"><span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37]">{it.type.replace(/_/g, ' ')}</span></td>
              <td className="px-4 py-2 text-white">{it.recipient_name || '—'}</td>
              <td className="px-4 py-2 text-[#A3A3A3] text-xs">{it.reference}</td>
              <td className="px-4 py-2 text-right text-[#D4AF37] font-bold">{money(it.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ───────── Page ─────────
const Payroll = () => {
  return (
    <div data-testid="admin-payroll-page" className="space-y-6">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-2"><Wallet className="w-6 h-6 text-[#D4AF37]" /> Payroll & Payouts</h2>
        <p className="text-[#A3A3A3]">Bonuses · VIP payouts · Customer credits · Payroll export · 1099 · History</p>
      </div>
      <Tabs defaultValue="bonuses">
        <TabsList className="bg-[#0A0A0A] border border-[#262626] flex-wrap h-auto p-1">
          <TabsTrigger data-testid="tab-bonuses" value="bonuses" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]"><Award className="w-3.5 h-3.5 mr-1" /> Bonuses</TabsTrigger>
          <TabsTrigger data-testid="tab-vip" value="vip" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]"><Users className="w-3.5 h-3.5 mr-1" /> VIP Payouts</TabsTrigger>
          <TabsTrigger data-testid="tab-credits" value="credits" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]"><Receipt className="w-3.5 h-3.5 mr-1" /> Customer Credits</TabsTrigger>
          <TabsTrigger data-testid="tab-payroll" value="payroll" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]"><Calendar className="w-3.5 h-3.5 mr-1" /> Payroll Export</TabsTrigger>
          <TabsTrigger data-testid="tab-1099" value="1099" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]"><Award className="w-3.5 h-3.5 mr-1" /> 1099 Tracking</TabsTrigger>
          <TabsTrigger data-testid="tab-history" value="history" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]"><History className="w-3.5 h-3.5 mr-1" /> History</TabsTrigger>
        </TabsList>
        <TabsContent value="bonuses" className="mt-4"><BonusesTab /></TabsContent>
        <TabsContent value="vip" className="mt-4"><VipPayoutsTab /></TabsContent>
        <TabsContent value="credits" className="mt-4"><CreditsTab /></TabsContent>
        <TabsContent value="payroll" className="mt-4"><PayrollExportTab /></TabsContent>
        <TabsContent value="1099" className="mt-4"><Tax1099Tab /></TabsContent>
        <TabsContent value="history" className="mt-4"><HistoryTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default Payroll;
